import { gql, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, BodyText, TextArea } from '../../../../common/src/components/core'
import { TestIds } from '../../../../common/src/data/testids'
import { diffFmt } from '../../../../common/src/date'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import memoize from '../../../../common/src/memoize'
import {
  FetchNotesQuery,
  FetchNotesQueryVariables,
  FetchUserContextQuery,
  ModelType,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import EditBoxLarge from './EditBoxLarge'
import LazyComponent from './LazyComponent'

export const fetchNotesQuery = gql`
  query FetchNotes($parentId: Int!, $type: ModelType!) {
    notes(parentId: $parentId, noteType: $type) {
      ...Note
    }
  }
  ${Fragments.note}
`

const updateNoteMutation = gql`
  mutation UpdateNote($id: Int!, $body: String) {
    updateNote(id: $id, body: $body) {
      ...Note
    }
  }
  ${Fragments.note}
`

export interface NotesSectionProps {
  user: FetchUserContextQuery['self']
  firmId?: number
  taskId?: number
  contactId?: number
  showingId?: number
}

export class NotesSectionImpl extends React.PureComponent<
  NotesSectionProps & QueryResult<FetchNotesQuery, FetchNotesQueryVariables>,
  {
    canSend: boolean
    hasError: boolean
    isExpanded: boolean
    editNoteId?: number // which note is being edited
  }
> {
  private editHandlers = {}
  private focusHandlers = {}
  private blurHandlers = {}
  state = { canSend: false, hasError: false, isExpanded: false, editNoteId: undefined }

  componentDidMount() {
    keyboard.addListener(this.onKeyShortcutBound)
  }

  componentWillUnmount() {
    keyboard.removeListener(this.onKeyShortcutBound)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const { isExpanded, editNoteId } = this.state
    const { user, data, loading } = this.props
    if (loading) {
      return <BodyText>Loading notes…</BodyText>
    }
    if (data == null) {
      return null
    }
    const { notes } = data
    const isSuper = user.permissions.canModifyNotes

    return (
      <NotesSectionWrap>
        {notes &&
          notes
            .map(n => (
              <EditBoxLarge
                key={'note-' + n.id}
                borderColor={n.messageId ? 'midway' : 'steel'}
                label={n.messageId ? n.agentName : n.author.name}
                sublabel={<NoteSubLabel note={n} />}
                onFocus={memoize(this.focusHandlers, n.id, (a: number) => () => this.onFocus(a))}
                onBlur={memoize(this.blurHandlers, n.id, () => () => this.onBlurBound())}
                canEdit={isSuper || n.author.id === user.id}
                canDelete={isSuper || n.author.id === user.id}
                isEditing={editNoteId === n.id}
                confirmDelete
                deletePrompt="Delete note?"
                submitEdit={memoize(
                  this.editHandlers,
                  n.id,
                  (id: number) => (body: string | null) => this.editNoteBound(id, body)
                )}
                body={n.body}
                testid={TestIds.noteBody}
              />
            ))
            .concat(<Spacer key="spacer" height={16} />)}
        {isExpanded && (
          <NoteTextArea hasError={this.state.hasError} onKeyDown={this.onKeyBound} onKeyPress={this.onKeyBound} />
        )}
      </NotesSectionWrap>
    )
  }

  private onFocus(editNoteId: number) {
    this.setState({ editNoteId })
  }

  private onBlurBound = () => {
    this.setState({ editNoteId: undefined })
  }

  private onKeyShortcutBound = (action: KeyAction) => {
    if (action === KeyAction.esc) {
      this.setState({ isExpanded: false, hasError: false, canSend: false })
    }
  }

  private onKeyBound = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault()
    }
  }

  private editNoteBound = (id: number, body: string | null) => {
    apolloWebClient
      .mutate({
        mutation: updateNoteMutation,
        variables: { id, body },
      })
      .then(() => {
        this.setState({ editNoteId: undefined })
        console.log('Edited Note', { noteId: id, noteBody: body })
      })
      .catch(() => {
        this.setState({ hasError: true })
      })
  }
}

const NotesSectionWrap = styled.div``

const NoteTextArea = styled(TextArea)`
  width: 100%;
  border-radius: 4px;
  height: 64px;
  font-size: 14px;
`

export default function NotesSection(props: NotesSectionProps) {
  return <LazyComponent componentGenerator={LazyRenderedNotesSection(props)} stayVisible />
}

function LazyRenderedNotesSection(props: NotesSectionProps) {
  const { firmId, taskId, contactId, showingId } = props
  let variables: FetchNotesQueryVariables
  if (firmId) {
    variables = { type: ModelType.FIRM, parentId: firmId }
  } else if (taskId) {
    variables = { type: ModelType.TASK, parentId: taskId }
  } else if (contactId) {
    variables = { type: ModelType.CONTACT, parentId: contactId }
  } else if (showingId) {
    variables = { type: ModelType.SHOWING, parentId: showingId }
  } else {
    throw new Error('NotesSection missing parent ID')
  }

  // eslint-disable-next-line react/display-name
  return () => <NoteComponent {...props} variables={variables} />
}

export function NoteComponent(props: NotesSectionProps & { variables: FetchNotesQueryVariables }) {
  const res = useQuery<FetchNotesQuery, FetchNotesQueryVariables>(fetchNotesQuery, { variables: props.variables })
  if (res.loading || res.error) {
    return null
  }

  return <NotesSectionImpl {...props} {...res} />
}

interface NoteSubLabelProps {
  note: FetchNotesQuery['notes'][0]
}

const NoteSubLabel = (props: NoteSubLabelProps) => {
  const { note } = props
  const timeDiff = diffFmt(new Date(), note.timeCreated)

  // old note
  if (note.messageId == null) {
    return <>{`posted ${timeDiff} ago`}</>
  }

  // new note
  return (
    <>
      noted{' '}
      <Link
        color="sky"
        to={'/leasing/inbox/thread/' + note.threadId + '#message/' + note.messageId}
        testid={TestIds.noteMessageLink}
      >
        {timeDiff} ago
      </Link>
    </>
  )
}
